import { ampli } from 'lib-shared-tracking';
import type { InteractionEvent } from 'pixi.js';
import {
  createElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useEffectOnce } from 'react-use';

import { appActions } from 'app/app.actions';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { ContextMenu } from 'flow-builder/components/contextMenu/contextMenu';
import {
  EditorContext,
  EditorContextHandlers,
} from 'flow-builder/types/context';
import type { Relation } from 'flow-builder/types/relation';
import type { NodeArrangeMessage } from 'flow-builder/types/worker';
import { getConnections } from 'flow-builder/utils/node/getConnections/getConnections';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { getArrangeWorker } from './getWorker';

export type ContextMenuHideHandler = VoidFunction;
export type ContextMenuShowHandler = (
  id: Relation['id'],
  e: InteractionEvent,
) => void;

type UseContextMenuOptions = Pick<EditorContext, 'props' | 'state'> & {
  handlers: Pick<EditorContextHandlers, 'node'>;
};

type UseContextMenuReturn = {
  onContextMenuShow: ContextMenuShowHandler;
  onContextMenuHide: ContextMenuHideHandler;
  contextMenu: ReactNode;
};

type WorkerActions = NodeArrangeMessage['type'];

const workerTrackingEvents: Record<WorkerActions, ({ flowType }) => void> = {
  align: (properties) => ampli.flowContextAlignClicked(properties),
  arrange: (properties) => ampli.flowContextNodeArrangeClicked(properties),
};

export function useContextMenu({
  props,
  state,
  handlers,
}: UseContextMenuOptions): UseContextMenuReturn {
  const worker = useMemo(getArrangeWorker, []);
  const dispatch = useAppDispatch();
  const {
    nodeState: { relations },
    nodeState,
    setNodeState,
  } = state;
  const { flow } = props;
  const { onDeleteSelection } = handlers.node;
  const [loadingAction, setLoadingAction] = useState<WorkerActions | null>(
    null,
  );
  const [{ isVisible, position, nodeId }, setState] = useState<{
    isVisible: boolean;
    position: [number, number];
    nodeId: Relation['id'] | null;
  }>({
    isVisible: false,
    position: [0, 0],
    nodeId: null,
  });

  const onContextMenuHide = useCallback<ContextMenuHideHandler>(() => {
    setState({
      isVisible: false,
      position: [0, 0],
      nodeId: null,
    });
  }, []);

  const onContextMenuShow = useCallback<ContextMenuShowHandler>((id, event) => {
    if (!event) {
      return;
    }

    event.stopPropagation();

    const originalEvent = event.data.originalEvent as PointerEvent;

    setState({
      isVisible: true,
      position: [originalEvent.clientX, originalEvent.clientY],
      nodeId: id,
    });
  }, []);

  const createWorkerRequest = useCallback(
    (type: WorkerActions) => () => {
      if (!nodeId) return;

      workerTrackingEvents[type]({ flowType: flow.type });

      setLoadingAction(type);

      const message: NodeArrangeMessage = {
        type,
        payload: [nodeState, nodeId],
      };

      worker.postMessage(message);
    },
    [flow, nodeId, nodeState, worker],
  );

  const handleDelete = useCallback(async () => {
    onContextMenuHide();

    ampli.flowContextDeleteClicked({ flowType: flow.type });

    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Delete node?',
            description:
              "Are you sure you want to delete this node? Please note this action can't be undone.",
            confirm: {
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (!isConfirmed) {
      ampli.flowContextDeleteCancelClicked({ flowType: flow.type });
      return;
    }

    ampli.flowContextDeleteConfirmClicked({ flowType: flow.type });
    onDeleteSelection();
  }, [flow.type, onDeleteSelection, onContextMenuHide, dispatch]);

  const handleMessage = (event: MessageEvent<NodeArrangeMessage>) => {
    const { payload } = event.data;
    const [nodeState] = payload;

    // Ideally this would be a part of the worker.
    // However, workers are not able to accept functions / objects with functions,
    // therefore we can't pass `nodeAttributes` to it.
    nodeState.connections = getConnections(nodeState.relations);

    setNodeState(nodeState);
    setLoadingAction(null);
    onContextMenuHide();
  };

  useEffectOnce(() => {
    worker.addEventListener('message', handleMessage);

    return () => {
      worker.removeEventListener('message', handleMessage);
      worker.terminate();
    };
  });

  const selectedNodeRelation = relations[nodeId || ''];

  const contextMenu = useMemo(
    () =>
      isVisible
        ? createElement(ContextMenu, {
            handlers: {
              onAlign: createWorkerRequest('align'),
              onArrange: createWorkerRequest('arrange'),
              onDelete: handleDelete,
            },
            loading: {
              isAlignLoading: loadingAction === 'align',
              isArrangeLoading: loadingAction === 'arrange',
            },
            disabled: {
              isAlignDisabled: !selectedNodeRelation?.parentId,
              isArrangeDisabled: !selectedNodeRelation?.children.length,
              isDeleteDisabled:
                selectedNodeRelation?.nodeType?.toLowerCase() ===
                FlowNodeTypes.INIT,
            },
            position,
            onHide: onContextMenuHide,
          })
        : null,
    [
      isVisible,
      createWorkerRequest,
      handleDelete,
      loadingAction,
      position,
      onContextMenuHide,
      selectedNodeRelation,
    ],
  );

  return {
    onContextMenuShow,
    onContextMenuHide,
    contextMenu,
  };
}
